import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { css, keyframes } from '@emotion/css';
import CloseIcon from './CloseIcon';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import UserMessage from '../UserMessage/UserMessage';
import { get, invoke, isEmpty, toNumber, first, last, delay, includes } from 'lodash';
import BackIcon from './BackIcon';
import { isMobile, isMobileScreen } from '../../utils/utils';
import { BackButtonContext, BackButtonContextType } from '../../components/Order/OneTimeAddItems';

const modalStyle = css`
  padding: 1.5rem 1rem;
  width: fit-content;
  border-radius: 1rem;
  background: #fff;
  box-shadow: 0 0 2rem 0 rgba(56, 51, 46, 0.08);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  margin: 1.75rem auto;

  @media (max-width: 641px) {
    top: 0;
    left: 0;
    right: 0;
    transform: none;
    border-radius: 0;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden auto;
    margin: 0 auto;
  }
`;

const expandedModalStyle = css`
  @media (max-width: 641px) {
    position: absolute;
    bottom: 0;
    height: 100vh;

    div[role='tablist'] {
      flex: 0 1;
    }

    .sns-modal-content-wrapper {
      height: 100%;
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .sns-modal-content,
    .tabgroup,
    .tab-content,
    div[role='tabpanel'],
    div[role='tabpanel'] > div,
    .item-list-container {
      display: flex;
      height: 90%;
      flex-direction: column;
      flex: 1 1;
    }
  }
`;

const otaExpandedModalStyle = css`
  @media (max-width: 641px) {
    height: 90vh;
  }
`;

const scrollStyle = css`
  @media (max-width: 641px) {
    .sns-modal-content,
    .tabgroup,
    .tab-content,
    div[role='tabpanel'],
    div[role='tabpanel'] > div,
    .item-list-container {
      overflow: hidden auto;
    }
  }
`;

const scrollableExpandedModal = css`
  @media (max-width: 641px) {
    position: initial;
    height: initial;
  }
`;

const openAnimSide = keyframes`
    from {transform: translateY(100%);}
    to {transform: translateY(0);}
`;

const closeAnimSide = keyframes`
    from {transform: translateY(100%);}
    to {transform: translateY(10%);}
`;

// const overlayStyle = (showBottomTransitionModal) => {
//   const transitionTime = 0.2;
//   return css`
//     display: block;
//     position: fixed;
//     background: rgba(83, 82, 80, 0.5);
//     top: 0;
//     bottom: 0;
//     left: 0;
//     right: 0;
//     z-index: 1000;
//     overflow: auto;

//     @media (max-width: 641px) {
//       ${showBottomTransitionModal &&
//       `
//    animation: ${openAnimSide} ${transitionTime}s ease both;
//    box-shadow: 0 -4px 16px rgba(56, 51, 46, 0.16);
//    border-top-left-radius: 2rem;
//    border-top-right-radius: 2rem;
//    height: 90vh
//   `}
//     }
//   `;
// };

const overlayStyle = (showBottomTransitionModal, halfHeightMobileModalPerc, modalOverflowValue) => {
  const transitionTime = 0.5;
  return css`
    //   display: block;
    position: fixed;
    background: rgba(83, 82, 80, 0.5);
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1000;
    overflow: auto;

    @media (max-width: 641px) {
      ${showBottomTransitionModal &&
      `
   animation: ${openAnimSide} ${transitionTime}s ease both;
   box-shadow: 0 -4px 16px rgba(56, 51, 46, 0.16);
   border-top-left-radius: 2rem;
   border-top-right-radius: 2rem;
   height: ${halfHeightMobileModalPerc ? halfHeightMobileModalPerc : '90'}%;
   top: auto;
   overflow: ${modalOverflowValue ? modalOverflowValue : 'auto'};
  `}
    }
  `;
};

const modalHeaderStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
`;

const titleTextStyle = css`
  position: relative;
  bottom: 0.1875rem;
  font-size: 1.375rem !important;
  font-weight: 600 !important;
  line-height: 2.125rem !important;
`;

const titleTextCenterStyle = css`
  margin-left: auto;
  margin-right: auto;
`;

const titleTextCenterForOtaToSnsModalStyle = css`
  @media (max-width: 641px) {
    margin-left: auto;
    margin-right: auto;
    font-size: 18px !important;
  }
`;

const oneTimeAddModalTitleTextStyle = css`
  @media (max-width: 641px) {
    font-size: 1rem !important;
  }
`;
const driverTipInfoModalTitleTextStyle = css`
  @media (max-width: 641px) {
    font-size: 1.125rem !important;
    margin-top: 2px;
  }
`;

const descriptionStyle = css`
  margin-bottom: 1.5rem;
  margin-top: 0.625rem;
  font-size: 1rem;
  line-height: 1.5rem;
`;
const descriptionStyleOtaToSnsModalStyle = css`
  margin-top: 0;
  position: relative;
  top: -0.625rem;
  @media (max-width: 641px) {
    display: flex;
    text-align: center;
  }
`;
const removeModeldescription = css`
  text-align: center;
  margin-bottom: 1rem;
`;

const Description = styled.div`
  max-width: ${(props: { maxWidth: number }) => `${props.maxWidth}rem`};
`;

const strokeStyle = css`
  height: 0.0625rem;
  background: #f1f0ee;
  margin-bottom: 1rem;
`;

const strokeStyleOtaToSnsModal = css`
  @media (max-width: 641px) {
    height: 0;
    margin-bottom: 0;
    background: none;
  }
`;

const closeButtonStyle = css`
  cursor: pointer;
  background: white;
  border: none;
  width: fit-content;
`;

const BackButtonStyle = css`
  cursor: pointer;
  background: white;
  border: none;
  width: fit-content;
`;

export type ModalContextType = {
  setModalError?: (val: string) => void;
  setScrollable?: (scrollable: boolean) => void;
};

export const ModalContext = createContext<ModalContextType | null>(null);

const Modal = ({
  titleText,
  description,
  showRemoveItemModal = false,
  titleTextCenter = false,
  onClose,
  show,
  showBottomTransitionModal,
  halfHeightMobileModalPerc = '',
  modalOverflowValue = '',
  children,
  maxWidth,
  fillScreen,
  overFlowBodyHidden = false,
  className,
  isAvailableSlotsModal = false,
  availableSlotsModalTitleClass,
  isOneTimeAddsModal = false,
  isDriverTipInfoModal = false,
  isOtaToSnsModal = false,
  hideCloseButton = false,
  modalId = '',
  titleTextCenterForOtaToSnsModal = false,
  showStrokeLines = true,
}) => {
  const [modalError, setModalError] = useState('');
  const [scrollable, setScrollable] = useState(false);
  const modalContentRef = useRef(null);
  const [shouldFillScreen, setShouldFillScreen] = useState(false);
  const overlayRef = useRef(null);
  const context = useContext(BackButtonContext) as BackButtonContextType;

  const showBackButton = get(context, 'showBackButton', false);
  const handleBackButtonClick = get(context, 'handleBackButtonClick', () => {});

  useEffect(() => {
    setModalError('');

    if (show) {
      if (overFlowBodyHidden) {
        isMobileScreen() || isMobile() ? (document.body.style.overflow = 'hidden') : '';
      }
      const screenHeight = document.documentElement.clientHeight;
      const modalRect = invoke(modalContentRef.current, 'getBoundingClientRect');
      setShouldFillScreen(fillScreen || (toNumber(get(modalRect, 'height')) * 100) / toNumber(screenHeight) > 60);

      delay(trapFocus, 0);
    }
    return () => {
      if (overFlowBodyHidden) document.body.style.overflow = '';
    };
  }, [show]);

  const trapFocus = () => {
    const focusable: string =
      'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableElements: HTMLElement[] = invoke(modalContentRef, 'current.querySelectorAll', focusable);

    invoke(modalContentRef.current, 'addEventListener', 'focusout', (evt: FocusEvent) => {
      const focusableElements: HTMLElement[] = invoke(modalContentRef, 'current.querySelectorAll', focusable);

      if (evt.target === first(focusableElements) && !includes(focusableElements, evt.relatedTarget)) {
        delay(() => invoke(last(focusableElements), 'focus', { focusVisible: true }), 0);
      }

      if (evt.target === last(focusableElements) && !includes(focusableElements, evt.relatedTarget)) {
        delay(() => invoke(first(focusableElements), 'focus', { focusVisible: true }), 0);
      }
    });

    invoke(first(focusableElements), 'focus', { focusVisible: true });
  };

  const overlayClick = (evt) => {
    if (evt.target === overlayRef.current) {
      onClose();
    }
  };

  if (!show) return null;

  const portalElement = document.getElementById('sns-portal') as HTMLElement;

  return ReactDOM.createPortal(
    <ModalContext.Provider value={{ setModalError, setScrollable }}>
      <div
        className={overlayStyle(showBottomTransitionModal, halfHeightMobileModalPerc, modalOverflowValue)}
        onClick={overlayClick}
        ref={overlayRef}
        role={'dialog'}
        aria-label={titleText}
        data-testid="modal"
        id={modalId}
      >
        <div
          className={`${modalStyle} ${className} ${shouldFillScreen ? expandedModalStyle : ''} ${isOneTimeAddsModal || isOtaToSnsModal ? otaExpandedModalStyle : ''} ${!isOneTimeAddsModal ? scrollStyle : ''} ${scrollable ? scrollableExpandedModal : ''}`}
          ref={modalContentRef}
        >
          {isEmpty(modalError) ? (
            <div className="sns-modal-content-wrapper">
              <div className={modalHeaderStyle}>
                {showBackButton && (
                  <button className={BackButtonStyle} onClick={handleBackButtonClick}>
                    {BackIcon}
                  </button>
                )}
                <h1
                  className={`${titleTextStyle} ${isOneTimeAddsModal ? oneTimeAddModalTitleTextStyle : ''} ${isDriverTipInfoModal ? driverTipInfoModalTitleTextStyle : ''} ${isAvailableSlotsModal ? availableSlotsModalTitleClass : ''} ${titleTextCenter && titleTextCenterStyle} ${titleTextCenterForOtaToSnsModal ? titleTextCenterForOtaToSnsModalStyle : ''}`}
                  role="heading"
                >
                  {titleText}
                </h1>
                {!hideCloseButton && (
                  <button aria-label="Close" data-testid="Close" className={closeButtonStyle} onClick={onClose}>
                    {CloseIcon}
                  </button>
                )}
              </div>
              {!showRemoveItemModal && showStrokeLines && <div className={strokeStyle} />}

              {description && (
                <Description maxWidth={maxWidth}>
                  <p
                    className={`${descriptionStyle} ${showRemoveItemModal && removeModeldescription} ${titleTextCenterForOtaToSnsModalStyle ? descriptionStyleOtaToSnsModalStyle : ''}`}
                  >
                    {description}
                  </p>
                  {!showRemoveItemModal && (
                    <div className={`${strokeStyle} ${!showStrokeLines ? strokeStyleOtaToSnsModal : ''}`}></div>
                  )}
                </Description>
              )}

              <div className="sns-modal-content">{children}</div>
            </div>
          ) : (
            <div>
              <div className={modalHeaderStyle}>
                <p className={titleTextStyle}>Error</p>
                <div data-testid="Close" className={closeButtonStyle} onClick={onClose}>
                  {CloseIcon}
                </div>
              </div>
              <div className={strokeStyle} />

              <UserMessage messageType="warning" messageText={modalError} />
            </div>
          )}
        </div>
      </div>
    </ModalContext.Provider>,
    portalElement,
  );
};

Modal.propTypes = {
  titleText: PropTypes.string.isRequired,
  titleTextCenter: PropTypes.bool,
  showRemoveItemModal: PropTypes.bool,
  description: PropTypes.string,
  className: PropTypes.string,
  onClose: PropTypes.func,
  show: PropTypes.bool,
  maxWidth: PropTypes.number,
  children: PropTypes.any,
  fillScreen: PropTypes.bool,
  overFlowBodyHidden: PropTypes.bool,
  showBottomTransitionModal: PropTypes.bool,
  isAvailableSlotsModal: PropTypes.bool,
  availableSlotsModalTitleClass: PropTypes.string,
  isOneTimeAddsModal: PropTypes.bool,
  isDriverTipInfoModal: PropTypes.bool,
  showBackButton: PropTypes.bool,
  onBackButtonClick: PropTypes.func,
  hideCloseButton: PropTypes.bool,
  modalId: PropTypes.string,
  titleTextCenterForOtaToSnsModal: PropTypes.bool,
  showStrokeLines: PropTypes.bool,
  isOtaToSnsModal: PropTypes.bool,
  modalOverflowValue: PropTypes.string,
};

export default Modal;

/*
Modal Component Improvements:
1. Modal title and close should be aligned to the center
2. Modal title should be optimized for the mobile view in terms of font size and padding
 */
